/**
 * L0 — the simplest mod sync core (design/mod-core-from-scratch.md §1).
 *
 * A configured automerge-repo `Repo`, nothing more. CRUD + cross-client sync
 * are then the STOCK repo API (`create` / `find` / `handle.change` / `delete` /
 * `handle.on('change')`); subduction is the transport when `endpoints` are
 * given. Everything mod-specific layers on top of this factory (worker hosting
 * L1, log model L2, bootstrap L3, …) — nothing reaches around it.
 *
 * Deliberate choices (design decisions, not omissions):
 * - **Capability access (§10) is the Repo's own default.** Pre-launch there is
 *   no server-enforced per-doc authorization; the stock default shareConfig
 *   ({ announce/access: () => true }) IS the capability model, so we pass
 *   nothing. Scoping what a client *chooses* to sync is the caller's scale
 *   concern (bootstrap L3), not an access gate here.
 * - **Storage is injected, and optional.** Browser passes IndexedDB, servers pass
 *   NodeFS; omitting it is the stock ephemeral repo (tests, throwaway peers) —
 *   no in-memory adapter needed, the Repo already supports storage-less.
 * - **Slim import + explicit WASM init (patchwork-faithful).** This module
 *   imports `@automerge/automerge-repo/slim`, which does NOT auto-init wasm at
 *   module eval (the fullfat entry's environment-detection hangs silently in
 *   workers). Embedders init explicitly before constructing:
 *   `initializeBase64Wasm(automergeWasmBase64)` + subduction `initSync`.
 * - The subduction construction below (endpoints) is the shape proven against a
 *   live relay by `spikes/rfc0250-p0-client.mjs` (R0). There are no sync
 *   intervals to pass: this build of the engine ignores `periodicSyncInterval`
 *   and `batchSyncInterval` and runs no background sync at all. A document syncs
 *   when it is written, when the connection comes back, or when a failed sync is
 *   retried (RFC 0310 §1b).
 */

import type {
  NetworkAdapterInterface,
  PeerId,
  ShareConfig,
  StorageAdapterInterface,
  SubductionTimeouts,
  WebSocketEndpointInterface,
} from '@automerge/automerge-repo/slim'
import { Repo } from '@automerge/automerge-repo/slim'
import { followMoves, type MoveMemory } from './follow-moves'

/** Peer ids a window's tab repo connects with (RFC 0317 3d). */
export const TAB_PEER_PREFIX = 'tab-'

/**
 * Send a peer only what it asks for (RFC 0317 3d).
 *
 * The stock policy announces every open document to every peer. Pointed
 * downstream that is a memory leak: a fresh peer on the daemon's sync socket
 * was sent 193 documents it never asked for (measured Oct 7), and the shared
 * worker pulled every one in full. A peer `downstream` says is downstream gets
 * a document once it requests it, and every change after that. Upstream
 * peers still hear about everything, so a document made here still reaches
 * them. Access is unchanged: whoever asks, gets.
 */
export function interestShareConfig(
  downstream: (peerId: string) => boolean,
): ShareConfig {
  return {
    announce: async (peerId) => !downstream(String(peerId)),
    access: async () => true,
  }
}

/** The fork's subduction adapter-tunnel entry (RepoConfig['subductionAdapters']). */
export interface SubductionAdapterConfig {
  adapter: NetworkAdapterInterface
  serviceName: string
  /** Initiate ("connect") or accept ("accept") the handshake. Default "connect". */
  role?: 'connect' | 'accept'
}

export interface CreateModRepoOptions {
  /**
   * Where this repo keeps the moves it has learned across restarts (RFC 0346),
   * so a known old id is never asked for, even before the World is read.
   */
  moveMemory?: MoveMemory
  /** NATIVE subduction relay endpoint(s) — bare WebSocket wire (the R0-proven
   *  shape vs the I&S relay). Omit if using `subductionAdapters`. */
  endpoints?: Array<string | WebSocketEndpointInterface>
  /** ADAPTER-TUNNEL subduction — the handshake tunneled over any automerge-repo
   *  NetworkAdapter (the P1·b-proven shape vs mod-sync-server's /subduction
   *  acceptor; clients pass a BrowserWebSocketClientAdapter with role
   *  "connect"). This is how mod clients reach OUR server today. */
  subductionAdapters?: SubductionAdapterConfig[]
  /** Storage adapter (IndexedDB in the browser, NodeFS on servers). Omit = ephemeral. */
  storage?: StorageAdapterInterface
  /** Extra network adapters (e.g. a MessageChannel to the worker/tab peer). */
  network?: NetworkAdapterInterface[]
  /** Stable peer id; default is repo-generated. */
  peerId?: PeerId
  /** Override the capability default. */
  shareConfig?: ShareConfig
  /** Override any of the Subduction deadlines; see `MOD_SUBDUCTION_TIMEOUTS`. */
  subductionTimeouts?: SubductionTimeouts
  /** Encoded frame ceiling (RFC 0345 §3f). `false` explicitly selects legacy
   * wire for a known old relay; a failed bounded request never downgrades. */
  subductionReceiveBudgetBytes?: number | false
}

/**
 * One MiB per encoded frame. Sixteen frames occupy at most half the daemon's
 * 32 MiB receive queue, leaving headroom for protocol traffic. This is a frame
 * limit, not a bound on the assembled document or the client's total memory.
 */
export const MOD_SUBDUCTION_RECEIVE_BUDGET_BYTES = 1024 * 1024

/**
 * The Subduction deadlines every Mod peer runs with.
 *
 * THE DEFAULTS WERE DISCARDING DELIVERED DATA. With them — 30 s for every
 * roundtrip, 60 s per sync round — a browser worker logged, in one session:
 *
 *   515x  BatchSyncResponse had no pending caller
 *   458x  request timed out
 *     4x  heal EXHAUSTED ... after 10 attempts   (one of them the project registry)
 *
 * "No pending caller" is a reply arriving AFTER its request gave up. The relay
 * was answering; this peer had stopped waiting, so the answer was dropped. That
 * one mechanism is every symptom chased this week: a push whose acknowledgement
 * is dropped never counts as sent, so a run sits in the window and reads "waiting
 * for a worker" while the daemon never sees it; a pull whose reply is dropped
 * leaves a document stale; the heal loop retries into the same deadline ten times
 * and gives up.
 *
 * Replies are late, not lost, because a sync round is one request per document
 * and a project holds hundreds, all sharing one connection: the later ones pass
 * their deadline before their turn is served. A daemon full sync on this store
 * took 501 s end to end.
 *
 * So the deadlines are raised to outlast that queue rather than cut it short.
 * The cost is honest and accepted: a connection that is genuinely dead takes
 * longer to be declared so. A slow answer that arrives is worth more than a fast
 * failure that throws the answer away.
 */
export const MOD_SUBDUCTION_TIMEOUTS: Required<
  Pick<SubductionTimeouts, 'defaultMs' | 'syncMs'>
> = {
  /** Every internal roundtrip. Four times the 30 s built-in. */
  defaultMs: 120_000,
  /** One sync round to a peer set. Three times the 60 s default. */
  syncMs: 180_000,
}

/**
 * The `RepoConfig` `createModRepo` builds, as a pure function.
 *
 * Split out so what a peer is actually configured with is testable without a
 * relay or wasm — in particular that the Subduction deadlines reach the Repo,
 * which is the one setting whose absence silently discarded delivered data.
 */
export function repoConfigFor(
  options: CreateModRepoOptions = {},
): ConstructorParameters<typeof Repo>[0] {
  const { endpoints, storage, network, peerId, shareConfig } = options
  const hasSubduction =
    (endpoints && endpoints.length > 0) ||
    (options.subductionAdapters && options.subductionAdapters.length > 0)
  const receiveBudgetBytes =
    options.subductionReceiveBudgetBytes === false
      ? undefined
      : (options.subductionReceiveBudgetBytes ??
        MOD_SUBDUCTION_RECEIVE_BUDGET_BYTES)
  if (
    hasSubduction &&
    receiveBudgetBytes !== undefined &&
    (!Number.isSafeInteger(receiveBudgetBytes) ||
      receiveBudgetBytes < 1024 ||
      receiveBudgetBytes > MOD_SUBDUCTION_RECEIVE_BUDGET_BYTES)
  ) {
    throw new RangeError(
      'Subduction receive budget must be 1024..1048576 bytes',
    )
  }
  return {
    ...(storage ? { storage } : {}),
    ...(network ? { network } : {}),
    // §10 capability model: the Repo's own default shareConfig is already
    // { announce: () => true, access: () => true } — omit = capability.
    ...(shareConfig ? { shareConfig } : {}),
    ...(peerId ? { peerId } : {}),
    ...(endpoints && endpoints.length > 0
      ? { subductionWebsocketEndpoints: endpoints }
      : {}),
    ...(options.subductionAdapters && options.subductionAdapters.length > 0
      ? { subductionAdapters: options.subductionAdapters }
      : {}),
    ...(hasSubduction
      ? {
          ...(receiveBudgetBytes === undefined
            ? {}
            : { subductionReceiveBudgetBytes: receiveBudgetBytes }),
          subductionTimeouts: {
            ...MOD_SUBDUCTION_TIMEOUTS,
            ...options.subductionTimeouts,
          },
        }
      : {}),
  } as ConstructorParameters<typeof Repo>[0]
}

/** Build the one mod Repo. See module doc; this is the entire L0 core. */
export function createModRepo(options: CreateModRepoOptions = {}): Repo {
  // A document that says it moved is followed on every read (RFC 0346 3f).
  return followMoves(new Repo(repoConfigFor(options)), options.moveMemory)
}
